// PlayFirst Parent subscription card — three Paystack plans + a status
// strip + cancel-request flow.
//
// Surfaces:
//   • Current status (active / inactive / cancellation pending)
//   • Three Paystack plans (Monthly / Annual / Lifetime) — only shown
//     while the parent is *not* active, so we don't tempt resubscribers
//     into double-paying. Active parents see a "Manage / cancel" panel.
//   • Cancel-my-subscription button that opens a confirm dialog and
//     posts to /api/parent-app/subscription/cancel-request.
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Sparkles, Heart, ExternalLink, ArrowRight, Crown, CheckCircle2, Loader2, XCircle, Clock,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { PFP } from './styles';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const PLANS = [
  {
    id: 'monthly', name: 'Monthly', price: '$6.99', cadence: 'per month',
    blurb: 'Easy in, easy out. Pause anytime.',
    href: 'https://paystack.shop/pay/3y36h9xeh7',
    icon: <Sparkles className="w-5 h-5" />,
    accent: 'from-amber-50 to-white border-amber-200',
    pill: 'bg-amber-100 text-amber-800',
  },
  {
    id: 'annual', name: 'Annual', price: '$59.99', cadence: 'per year',
    blurb: 'Save $24 vs paying monthly.',
    href: 'https://paystack.shop/pay/iq5zgoi-ii',
    icon: <Heart className="w-5 h-5" />,
    accent: 'from-rose-50 to-white border-rose-200',
    pill: 'bg-rose-100 text-rose-800',
    badge: 'Best value',
  },
  {
    id: 'lifetime', name: 'Lifetime', price: '$119.99', cadence: 'once',
    blurb: 'Pay once. Yours for the long play.',
    href: 'https://paystack.shop/pay/-0ecc21xlr',
    icon: <Crown className="w-5 h-5" />,
    accent: 'from-violet-50 to-white border-violet-200',
    pill: 'bg-violet-100 text-violet-800',
    badge: 'No renewals',
  },
];

export default function ParentSubscriptionCard() {
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCancel, setShowCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);

  const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const fetchSub = async () => {
    try {
      const r = await axios.get(`${API}/parent-app/subscription`, auth());
      setSub(r.data);
    } catch {
      setSub({ status: 'inactive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSub(); }, []);

  const requestCancel = async () => {
    setCancelling(true);
    try {
      await axios.post(
        `${API}/parent-app/subscription/cancel-request`,
        { reason: cancelReason.trim() },
        auth(),
      );
      toast.success("Cancellation requested — we'll be in touch within 24h");
      setShowCancel(false);
      setCancelReason('');
      await fetchSub();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't submit cancellation — please email michaela@playfirstapp.com");
    } finally {
      setCancelling(false);
    }
  };

  const status = (sub?.status || '').toLowerCase();
  const isActive = status === 'active';
  const cancelPending = Boolean(sub?.cancel_requested_at);
  const planLabel = sub?.plan ? sub.plan.charAt(0).toUpperCase() + sub.plan.slice(1) : null;

  return (
    <div className={`${PFP.card} p-6 space-y-4`} data-testid="parent-subscription-card">
      <div>
        <h2 className="font-serif text-xl text-[#3a2f4f]">Your PlayFirst Parent plan</h2>
        <p className="text-sm text-stone-600 mt-1 leading-relaxed">
          Manage your plan or see what&apos;s included.{' '}
          <Link to="/parent-app/pricing" className="text-[#7C6F9C] underline" data-testid="parent-subscription-pricing-link">
            See everything included
          </Link>.
        </p>
      </div>

      {/* Status strip */}
      {loading ? (
        <div className="flex items-center gap-2 text-stone-500 text-sm" data-testid="parent-sub-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading your plan…
        </div>
      ) : (
        <div
          className={`rounded-2xl border p-4 flex items-start gap-3 ${
            isActive ? 'bg-emerald-50/60 border-emerald-200' : 'bg-stone-50 border-stone-200'
          }`}
          data-testid="parent-sub-status-strip"
        >
          {isActive ? (
            <CheckCircle2 className="w-5 h-5 text-emerald-700 shrink-0 mt-0.5" />
          ) : (
            <XCircle className="w-5 h-5 text-stone-400 shrink-0 mt-0.5" />
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-[#3a2f4f]">
              {isActive
                ? `Active${planLabel ? ` · ${planLabel} plan` : ''}`
                : 'Not active yet'}
            </p>
            <p className="text-xs text-stone-600 mt-0.5 leading-relaxed">
              {cancelPending
                ? 'Cancellation requested — you keep access until the end of your current period. We\u2019ll be in touch within 24h.'
                : isActive
                  ? "You're all set. Choose the cancel option below if you'd like to stop renewals."
                  : "Pick a plan below to activate your account."}
            </p>
            {cancelPending && (
              <p className="text-xs text-amber-700 mt-1 inline-flex items-center gap-1" data-testid="parent-sub-cancel-pending">
                <Clock className="w-3 h-3" /> Cancellation pending
              </p>
            )}
          </div>
        </div>
      )}

      {/* Plan cards — only when not active OR when active and lifetime (lifetime can still buy more for family/gift) */}
      {!isActive && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {PLANS.map((p) => (
            <a
              key={p.id} href={p.href} target="_blank" rel="noopener noreferrer"
              data-testid={`parent-plan-${p.id}`}
              className={`group relative rounded-2xl border bg-gradient-to-br ${p.accent} p-4 hover:shadow-md transition-all flex flex-col`}
            >
              {p.badge && (
                <span className={`absolute -top-2 right-3 text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full ${p.pill}`}>
                  {p.badge}
                </span>
              )}
              <div className={`w-9 h-9 rounded-xl ${p.pill} flex items-center justify-center mb-2`}>
                {p.icon}
              </div>
              <h3 className="font-serif text-lg text-[#3a2f4f]">{p.name}</h3>
              <p className="text-2xl font-serif text-[#3a2f4f] mt-1">
                {p.price}
                <span className="text-xs text-stone-500 font-sans ml-1">{p.cadence}</span>
              </p>
              <p className="text-sm text-stone-600 leading-relaxed mt-2 flex-1">{p.blurb}</p>
              <div className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-[#7C6F9C] group-hover:gap-2 transition-all">
                Choose this <ExternalLink className="w-3.5 h-3.5" />
              </div>
            </a>
          ))}
        </div>
      )}

      <Link to="/parent-app/pricing" className="text-xs text-[#7C6F9C] inline-flex items-center gap-1 hover:underline" data-testid="parent-subscription-see-all-link">
        See everything included in PlayFirst Parent <ArrowRight className="w-3 h-3" />
      </Link>

      {/* Cancel button — only visible when active and not already pending */}
      {isActive && !cancelPending && (
        <div className="pt-2">
          <button
            type="button"
            onClick={() => setShowCancel(true)}
            className="text-sm text-stone-500 hover:text-rose-700 underline underline-offset-2"
            data-testid="parent-sub-cancel-btn"
          >
            Cancel my subscription
          </button>
        </div>
      )}

      <p className="text-xs text-stone-400 leading-relaxed">
        Payments are securely processed by Paystack. No card details are stored by PlayFirst.
      </p>

      {/* Cancel dialog */}
      <Dialog open={showCancel} onOpenChange={(o) => !cancelling && setShowCancel(o)}>
        <DialogContent className="sm:max-w-md" data-testid="parent-sub-cancel-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-[#3a2f4f]">
              We&apos;re sorry to see you go 💛
            </DialogTitle>
            <DialogDescription className="text-sm text-stone-600 leading-relaxed pt-2">
              You&apos;ll keep full access until the end of your current billing period.
              We&apos;ll process the cancellation in Paystack and email you a confirmation
              within 24 hours.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2 mt-2">
            <label className="text-xs uppercase tracking-wider text-stone-500 font-semibold block">
              Any reason you&apos;d like to share? <span className="text-stone-400 normal-case font-normal">(optional)</span>
            </label>
            <Textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Cost, fit, taking a break, anything at all — we read every note."
              className="resize-none"
              rows={3}
              data-testid="parent-sub-cancel-reason"
            />
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2 mt-2">
            <Button
              variant="ghost"
              onClick={() => setShowCancel(false)}
              disabled={cancelling}
              data-testid="parent-sub-cancel-back-btn"
            >
              Keep my plan
            </Button>
            <Button
              variant="destructive"
              onClick={requestCancel}
              disabled={cancelling}
              data-testid="parent-sub-cancel-confirm-btn"
            >
              {cancelling && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {cancelling ? 'Requesting…' : 'Request cancellation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
